import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { cn } from './cn';

/**
 * Form fields — label, control, hint and error in one place, so every form looks and behaves the
 * same. They accept `react-hook-form`'s `register()` output directly (ref is a prop in React 19).
 */

interface FieldFrameProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

const controlClass = (error?: string) =>
  cn(
    'w-full rounded-lg border bg-surface px-3 text-sm text-ink placeholder:text-ink-muted/70',
    'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
    error ? 'border-red-500' : 'border-border',
  );

function Frame({
  id,
  label,
  error,
  hint,
  required,
  children,
}: FieldFrameProps & { id: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span aria-hidden className="text-red-600">
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="text-xs text-ink-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

const describedBy = (id: string, error?: string, hint?: string) =>
  error ? `${id}-error` : hint ? `${id}-hint` : undefined;

export type TextFieldProps = FieldFrameProps &
  InputHTMLAttributes<HTMLInputElement> & {
    /** Values offered while typing (a <datalist>); the field still takes any text. */
    suggestions?: readonly { value: string; label?: string }[];
  };

export function TextField({
  label,
  error,
  hint,
  required,
  className,
  id: idProp,
  suggestions,
  ...input
}: TextFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const listId = suggestions ? `${id}-suggestions` : undefined;
  return (
    <Frame id={id} label={label} error={error} hint={hint} required={required}>
      <input
        id={id}
        list={listId}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlClass(error), 'h-10', className)}
        {...input}
      />
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((o) => (
            <option key={o.value} value={o.value} label={o.label} />
          ))}
        </datalist>
      )}
    </Frame>
  );
}

export type TextAreaFieldProps = FieldFrameProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextAreaField({
  label,
  error,
  hint,
  required,
  className,
  id: idProp,
  ...area
}: TextAreaFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Frame id={id} label={label} error={error} hint={hint} required={required}>
      <textarea
        id={id}
        rows={4}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlClass(error), 'py-2', className)}
        {...area}
      />
    </Frame>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  /** Options with the same group are shown under one heading (optgroup). */
  group?: string;
}

/** Plain options first, then each group in order of first appearance. */
function renderOptions(options: readonly SelectOption[]) {
  const plain = options.filter((o) => !o.group);
  const groups = [...new Set(options.filter((o) => o.group).map((o) => o.group!))];
  const option = (o: SelectOption) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  );
  return (
    <>
      {plain.map(option)}
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {options.filter((o) => o.group === g).map(option)}
        </optgroup>
      ))}
    </>
  );
}

export type SelectFieldProps = FieldFrameProps &
  SelectHTMLAttributes<HTMLSelectElement> & {
    options: readonly SelectOption[];
    /** Adds an empty first option with this label (e.g. "— not set —" or "All"). */
    placeholder?: string;
  };

export function SelectField({
  label,
  error,
  hint,
  required,
  options,
  placeholder,
  className,
  id: idProp,
  ...select
}: SelectFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Frame id={id} label={label} error={error} hint={hint} required={required}>
      <select
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlClass(error), 'h-10', className)}
        {...select}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {renderOptions(options)}
      </select>
    </Frame>
  );
}

/** Compact select without a visible label (table cells, toolbars) — `label` is its accessible name. */
export function InlineSelect({
  label,
  options,
  className,
  ...select
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'aria-label'> & {
  label: string;
  options: readonly SelectOption[];
}) {
  return (
    <select
      aria-label={label}
      className={cn('h-8 rounded-lg border border-border bg-surface px-2 text-sm', className)}
      {...select}
    >
      {renderOptions(options)}
    </select>
  );
}

export type CheckboxFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  hint?: string;
};

/** One checkbox with its label (flags like "Financial management", "Important"). */
export function CheckboxField({ label, hint, className, id: idProp, ...input }: CheckboxFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 size-4 rounded border-border accent-brand-600"
        {...input}
      />
      <label htmlFor={id} className="text-sm">
        {label}
        {hint && <span className="block text-xs text-ink-muted">{hint}</span>}
      </label>
    </div>
  );
}

/** A set of checkboxes over the same value list (status filters, project pickers). */
export function CheckboxGroup<V extends string>({
  label,
  options,
  value,
  onChange,
  columns = false,
}: {
  label: string;
  options: readonly { value: V; label: string }[];
  value: readonly V[];
  onChange: (next: V[]) => void;
  /** Wrap into a scrollable column (long lists). */
  columns?: boolean;
}) {
  const toggle = (v: V, checked: boolean) => onChange(checked ? [...value, v] : value.filter((x) => x !== v));
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1 text-sm font-medium">{label}</legend>
      <div
        className={cn('flex gap-x-4 gap-y-1.5', columns ? 'max-h-64 flex-col overflow-y-auto' : 'flex-wrap')}
      >
        {options.map((o) => (
          <CheckboxField
            key={o.value}
            label={o.label}
            checked={value.includes(o.value)}
            onChange={(e) => toggle(o.value, e.target.checked)}
          />
        ))}
      </div>
    </fieldset>
  );
}

/** Compact text box without a visible label (table cells) — `label` is its accessible name. */
export function InlineTextField({
  label,
  className,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-label' | 'type'> & { label: string }) {
  return (
    <input
      type="text"
      aria-label={label}
      className={cn(
        'h-8 w-full rounded-lg border border-border bg-surface px-2 text-sm',
        'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
        className,
      )}
      {...input}
    />
  );
}

/** Checkbox without a visible label (table cells) — `label` is its accessible name. */
export function InlineCheckbox({
  label,
  className,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-label' | 'type'> & { label: string }) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      title={label}
      className={cn('size-4 rounded border-border accent-brand-600', className)}
      {...input}
    />
  );
}

/** A list filter: "All …" (empty value = no filter) plus the given choices. */
export function FilterSelect({
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  allLabel: string;
  options: readonly SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  return (
    <SelectField
      label={label}
      placeholder={allLabel}
      options={options}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}
